/**
 * Helpers de status de máquina.
 *
 * O backend pode entregar o status em vários formatos:
 *  - Português (Device State): "TRABALHANDO" | "PARADA" | "DESLIGADA" | ...
 *  - Inglês (API externa):     "ACTIVE" | "IDLE" | "RUNNING" | "ONLINE" | "STOPPED" |
 *                              "PAUSED" | "MAINTENANCE" | "REPAIR" | ...
 *
 * Internamente, a aplicação trabalha com 4 estados:
 *  "production" | "stopped" | "offline" | "maintenance"
 *
 * `toInternalStatus()` é IDEMPOTENTE: se receber um valor já interno, devolve
 * o mesmo valor. Isso evita o bug de "dupla conversão" em telas que pré-mapeiam
 * antes de armazenar e ainda re-mapeiam na hora de renderizar.
 */

export type InternalMachineStatus =
  | "production"
  | "stopped"
  | "offline"
  | "maintenance";

const INTERNAL_STATUSES: ReadonlySet<InternalMachineStatus> = new Set([
  "production",
  "stopped",
  "offline",
  "maintenance",
]);

export function isInternalStatus(
  value: unknown,
): value is InternalMachineStatus {
  return typeof value === "string" && INTERNAL_STATUSES.has(value as any);
}

/**
 * Converte qualquer representação conhecida de status para o formato interno.
 * Idempotente: aceita tanto valores brutos da API quanto valores já internos.
 */
export function toInternalStatus(
  value?: string | null,
): InternalMachineStatus {
  if (!value) return "offline";

  // Já está em formato interno -> devolve direto (idempotência)
  if (isInternalStatus(value)) return value;

  switch (String(value).toUpperCase()) {
    // Formato português (Device State)
    case "TRABALHANDO":
      return "production";
    case "PARADA":
      return "stopped";

    // Formato inglês (API externa / mapStatus legado)
    case "ACTIVE":
    case "RUNNING":
    case "ONLINE":
      return "production";
    case "IDLE":
    case "STOPPED":
    case "PAUSED":
      return "stopped";
    case "MAINTENANCE":
    case "REPAIR":
      return "maintenance";

    default:
      return "offline";
  }
}
